import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck } from "lucide-react";
import { FadeUp, Eyebrow } from "../components/motion";
import { API } from "../lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => {
        setPost(res.data);
        document.title = `${res.data.title} — Reef Deco Dubai`;
      })
      .catch(() => setPost(false))
      .finally(() => setLoaded(true));
  }, [slug]);

  return (
    <main data-testid="blog-post-page" className="min-h-screen bg-sand-bg">
      <header className="border-b border-sand-border bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="post-logo" className="flex items-center">
            <img src="/logo.png" alt="Reef Deco" className="h-9 w-auto" />
          </Link>
          <Link
            to="/blog"
            data-testid="post-back-blog"
            className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-reef hover:text-reef-dark transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
        </div>
      </header>

      {loaded && post === false && (
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <p className="font-serif text-3xl text-slate-900">Article not found</p>
          <Link to="/blog" className="mt-4 inline-block font-mono text-xs tracking-[0.18em] uppercase text-reef">
            ← Back to the journal
          </Link>
        </div>
      )}

      {post && (
        <article className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <FadeUp>
            <Eyebrow className="mb-4">{(post.created_at || "").slice(0, 10)} — Reef Deco Journal</Eyebrow>
            <h1 data-testid="post-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-slate-900">
              {post.title}
            </h1>
            {post.cover_image && (
              <div className="mt-8 overflow-hidden rounded-3xl border border-sand-border aspect-[16/9]">
                <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div data-testid="post-content" className="mt-10 space-y-5">
              {(post.content || "").split(/\n\s*\n/).map((para, i) => (
                <p key={i} className="text-base sm:text-lg text-slate-700 leading-relaxed whitespace-pre-line">
                  {para}
                </p>
              ))}
            </div>
            <div className="mt-14 bg-reef rounded-3xl p-8 sm:p-10 text-center">
              <p className="font-serif text-2xl sm:text-3xl text-sand-bg">Ready to transform your windows?</p>
              <p className="mt-2 text-sm text-sand-bg/75">Free visit, free measurement, installation in 1–3 days — anywhere in Dubai.</p>
              <Link
                to="/"
                data-testid="post-book-cta"
                className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-sand-bg text-reef font-mono text-xs tracking-[0.18em] uppercase hover:bg-white transition-colors"
              >
                <CalendarCheck className="w-4 h-4" /> Book a free visit
              </Link>
            </div>
          </FadeUp>
        </article>
      )}
    </main>
  );
}
